import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { PagingParams } from "../../types/pagination";
import { useNavigate, useLocation } from "react-router-dom";
import Paginator from "../common/Paginator";
import ConfirmDialog from "../common/ConfirmDialog";
import Table from "../common/Table";
import { useStore } from "../../app/stores/store";
import toast from "react-hot-toast";
import React from "react";
import { all_routes } from "../router/all_routes";
import { dialogFlags } from "../../constants/constants";
import TableFilterBar from "../common/TableFilterBar";
import LoadingSpinner from "../common/LoadingSpinner";

const BookingList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Single store destructuring
  const {
    bookingStore: {
      bookings,
      loadBookings,
      deleteBooking,
      pagination,
      setSearchTerm,
      setPagingParams,
      clearBookings,
      setDateFilter,
      setServiceFilter,
      setStatusCanceled,
      setStatusCompleted,
      setStatusFilter,
      setPaymentstatusFilter,
      setBookingCodeFilter,
    },
    clientStore: {
      currentClientItem,
      getClientItem,
    },
    userStore,
  } = useStore();

  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loadingClientItem, setLoadingClientItem] = useState(false);
  const [bookingCode, setBookingCode] = useState("");

  // Fetch client item on selectedId change with loading state
  useEffect(() => {
    if (selectedId) {
      setLoadingClientItem(true);
      getClientItem(selectedId).finally(() => setLoadingClientItem(false));
    }
  }, [selectedId, getClientItem]);

  const handleGetNext = (page: number) => {
    setPagingParams(new PagingParams(page, pageSize));
    loadBookings();
  };

  const handleSearch = () => {
    setSearchTerm(searchQuery);
    setBookingCodeFilter(bookingCode.trim());

    setPagingParams(new PagingParams(1, pageSize));
    loadBookings();
  };

  const handleReset = () => {
    setSearchTerm("");
    setSearchQuery("");
    setDateFilter("", "");
    setServiceFilter("");
    setStatusFilter("");
    setPaymentstatusFilter(""),
    setBookingCode("");
    setPagingParams(new PagingParams(1, pageSize));
    loadBookings();
  };

  const handleDelete = async () => {
    if (selectedId) {
      await deleteBooking(selectedId);
      loadBookings();
      navigate(location.pathname, { replace: true });
    }
  };

  const handleComplete = async () => {
    if (selectedId) {
      await setStatusCompleted(selectedId);
      loadBookings();
      navigate(location.pathname, { replace: true });
    }
  };

  const handleCancel = async () => {
    if (selectedId) {
      // await setStatusCanceled(selectedId);
      loadBookings();
      navigate(location.pathname, { replace: true });
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPagingParams(new PagingParams(1, newPageSize));
    loadBookings();
  };

  useEffect(() => {
    const handleClose = () => { };
    const modal = document.getElementById(dialogFlags.editDialog);
    modal?.addEventListener("hidden.bs.modal", handleClose);
    return () => modal?.removeEventListener("hidden.bs.modal", handleClose);
  }, []);

  useEffect(() => {
    if (!userStore.token) {
      clearBookings();
      navigate("/login");
      toast.error("Unauthorized");
    } else {
      loadBookings();
    }
  }, [userStore.token, loadBookings]);

  if (!bookings) return <LoadingSpinner />;

  return (
    <div className="col-lg-12">
      <div className="row">
        <div className="col-lg-12 d-flex">
          <div className="card book-card flex-fill mb-0 p-0">
            <TableFilterBar
              pagination={pagination}
              pageSize={pageSize}
              handlePageSizeChange={handlePageSizeChange}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              bookingCode={bookingCode}
              setBookingCode={setBookingCode}
              handleSearch={handleSearch}
              handleReset={handleReset}

            />
            <div className="flex flex-col card-body">
              <div className="table-responsive dashboard-table">
                <Table
                  dialogFlags={dialogFlags}
                  setSelectedId={setSelectedId}
                  exceptColumns={[
                    "id",
                    "aiResult",
                    "createDate",
                    "address",
                    "email",
                    "createdBy",
                    "updatedBy",
                    "reason",
                    "adultsNumber",
                    "childrenNumber",
                    "duration",
                    "processTime",
                    "carName",
                    "entryType",
                    "updateDate",
                    "isGccResident",
                    "nationalityName",
                    "refundRequests",
                    "clients",
                    "invoiceNo",
                    "bankCharge",
                    "securityDeposit",
                    "refundAmount",
                    "updatePaymentID",
                    "updatePaymentUserName",
                    "paymentType",
                    "servicePrice",
                    "childPrice",
                    "partnerPrice",
                    "partnerChildPrice",
                    "exception",
                    "airportUserPrice",
                    "airportUserChildPrice",
                    "hasNationalityPrice",
                    "childCenterFee",
                    "partnerCenterFee",
                    "partnerChildCenterFee",
                    "centerFee",
                    "createdByRoles",
                    "createdById"
                  ]}
                  data={bookings}
                  pageSize={pageSize}
                  rowsPerPageOptions={[10, 25, 50]}
                  routeUrl={all_routes.bookingDashboard}
                />
              </div>
              <div className="mx-auto pt-2">
                <Paginator handleGetNext={handleGetNext} pagination={pagination} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <div
        className="modal fade"
        id={dialogFlags.editDialog}
        tabIndex={-1}
        aria-labelledby="editModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="editModalLabel">
                Edit Booking
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              {selectedId && loadingClientItem ? (
                <div>Loading client details...</div>
              ) : selectedId && currentClientItem ? (
                <>
                  <div className="mb-2">
                    <strong>Name:</strong> {currentClientItem.name}
                  </div>
                  <div className="mb-2">
                    <strong>Status:</strong> {currentClientItem.status}
                  </div>
                  <div className="mb-2">
                    <strong>Created:</strong> {currentClientItem.createDate}
                  </div>
                </>
              ) : (
                <div>No client selected.</div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
              <button type="button" className="btn btn-primary">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        modalId={dialogFlags.deleteDialog}
        onConfirm={handleDelete}
        title="Confirm Delete"
        description="Are you sure you want to delete this booking?"
      />
      <ConfirmDialog
        modalId={dialogFlags.completeDialog}
        onConfirm={handleComplete}
        title="Confirm Setting As Completed"
        description="Are you sure you want to set this booking as completed?"
      />
      <ConfirmDialog
        modalId={dialogFlags.cancelDialog}
        onConfirm={handleCancel}
        title="Confirm Cancel"
        description="Are you sure you want to cancel this booking?"
      />
    </div>
  );
};

export default observer(BookingList);
